import { Avatar, type AvatarTone, Button, CameraIcon, cn, focusRing } from "@rhema/ui";
import { type ReactNode, useRef, useState } from "react";
import { photoForUpload } from "./photo";

/**
 * Chooses a photo for a space, channel or group, and removes it. The server crops it to a square.
 * Like profile photos, these are not end-to-end encrypted, and the picker says so.
 */
export function PhotoPicker({
  name,
  src,
  tone,
  initials,
  shape = "rounded",
  label,
  onPick,
  onRemove,
}: {
  name: string;
  src: string | null | undefined;
  tone: AvatarTone;
  initials?: string;
  shape?: "circle" | "rounded";
  /** What the photo is of, such as "the space". */
  label: string;
  onPick: (photo: string) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (job: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await job();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  let note: ReactNode = "Everyone who can see it sees this photo. It is not end-to-end encrypted.";
  if (busy) note = "Saving the photo";
  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        aria-label={src ? `Change the photo of ${label}` : `Add a photo for ${label}`}
        disabled={busy}
        onClick={() => input.current?.click()}
        className={cn(
          "relative flex-none cursor-pointer border-0 bg-transparent p-0 disabled:opacity-60",
          focusRing,
        )}
      >
        <Avatar
          name={name}
          src={src}
          tone={tone}
          size={64}
          shape={shape}
          {...(initials ? { initials } : {})}
        />
        <span className="absolute -right-1 -bottom-1 flex h-7 w-7 items-center justify-center rounded-full bg-blue text-white ring-2 ring-linen">
          <CameraIcon size={14} strokeWidth={2.2} />
        </span>
      </button>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
            {src ? "Change photo" : "Add a photo"}
          </Button>
          {src && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(onRemove)}>
              Remove
            </Button>
          )}
        </span>
        <span className="text-[12.5px] text-ink-secondary">{note}</span>
        {error && (
          <span role="alert" className="text-[12.5px] font-semibold text-scarlet">
            {error}
          </span>
        )}
      </span>
      <input
        ref={input}
        type="file"
        accept="image/*"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void run(async () => onPick(await photoForUpload(file)));
        }}
      />
    </div>
  );
}
